import React from 'react';
import { observer } from 'mobx-react';

import { constants } from '../../../config';
import { HomeNavbar } from '../../../common/home-navbar';
import { Footer } from '../../../common/footer';
import { SecondaryButton } from '../../../common/secondary-button';
import { Validator } from '../../../helpers/Validator';
import { MyAccountMenu } from '../my-account-menu';
import { SessionStore } from '../../../stores/SessionStore';
import { ScreenStore } from '../../../stores/ScreenStore';
import { USER_TYPES } from '../../../models/User';
import { routeNaming, AppRedirect, AppLink } from '../../../routes';
import { EditPassword } from '../edit-password';
import styles from './MyData.css';
import { SEO } from '../../../common/seo';

const SCREENS = {
  SHOW_DATA: 'showData',
  EDIT_DATA: 'editData',
  EDIT_PASSWORD: 'editPassword',
};

const MyData = observer(class MyData extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      actualScreen: SCREENS.SHOW_DATA,

      names: null,
      surnames: null,
      email: null,
      fictitiousName: null,
      tradeName: null,

      emailValid: true,
      socialNetworkInstagram: null,
      success: false,
    };

    this._changeInputValue = this._changeInputValue.bind(this);
    this._editPassword = this._editPassword.bind(this);
    this._editPasswordSuccess = this._editPasswordSuccess.bind(this);
    this._editOrCancel = this._editOrCancel.bind(this);
    this.saveChanges = this.saveChanges.bind(this);
    this._updateEmail = this._updateEmail.bind(this);
  }
  componentDidMount() {
    SessionStore.getUser();
  }
  componentWillUnmount() {
    SessionStore.resetEditUserErrors();
  }

  _editOrCancel() {
    this.setState(prevState => ({
      actualScreen: prevState.actualScreen !== SCREENS.SHOW_DATA
        ? SCREENS.SHOW_DATA : SCREENS.EDIT_DATA,
      names: SessionStore.user.names,
      surnames: SessionStore.user.surnames,
      email: SessionStore.user.email,
      fictitiousName: SessionStore.user.fictitiousName,
      tradeName: SessionStore.user.tradeName,
      socialNetworkInstagram: SessionStore.socialNetworks.instagram,
      success: false,
    }));
    SessionStore.resetEditUserErrors();
  }

  _editPasswordSuccess() {
    this.setState({
      actualScreen: SCREENS.SHOW_DATA,
      names: SessionStore.user.names,
      surnames: SessionStore.user.surnames,
      email: SessionStore.user.email,
      fictitiousName: SessionStore.user.fictitiousName,
      tradeName: SessionStore.user.tradeName,
      success: true,
    });
  }

  _editPassword() {
    this.setState({ actualScreen: SCREENS.EDIT_PASSWORD });
  }

  _changeInputValue(event) {
    const { target } = event;
    this.setState({ [target.name]: target.value });
  }

  _dataIsValid() {
    let specificDataIsValid = true;
    if (SessionStore.user.type === USER_TYPES.COMPANY) {
      specificDataIsValid = (
        !!this.state.fictitiousName &&
        !!this.state.tradeName
      );
    } else {
      specificDataIsValid = (
        !!this.state.names &&
        !!this.state.surnames
      );
    }
    return (
      !!this.state.email &&
      this.state.emailValid &&
      specificDataIsValid
    );
  }

  _updateEmail(event) {
    const { target: { value } } = event;
    this.setState({
      email: value,
      emailValid: Validator.isEmail(value),
    });
  }

  async saveChanges() {
    if (!this._dataIsValid()) {
      return;
    }
    const data = {
      ...SessionStore.user,
      names: this.state.names,
      surnames: this.state.surnames,
      email: this.state.email,
      fictitiousName: this.state.fictitiousName,
      tradeName: this.state.tradeName,
    };
    const dataSocialNetworks = [`instagram##${this.state.socialNetworkInstagram}`];
    await SessionStore.updateUser(data, dataSocialNetworks);
    if (SessionStore.updateSuccess) {
      this.setState({ actualScreen: SCREENS.SHOW_DATA, success: true });
      SessionStore.clearUpdateSuccess();
    }
  }

  render() {
    if (!SessionStore.loggedIn) {
      return (
        <AppRedirect routeName={routeNaming.HOME} />
      );
    }
    const canPartiallyEdit = SessionStore.user.isPartiallyEditable;

    const mobileVersion = ScreenStore.screenWidth <= constants.breakpoints.extraSmallMaxWidth;
    const fieldNameStyle = mobileVersion ? [styles.fieldName, styles.fieldNameMobile].join(' ') :
      styles.fieldName;
    const defaultValueStyle = [styles.fieldValue, styles.fieldValueInput].join(' ');
    const fieldValueStyle = this.state.actualScreen === SCREENS.EDIT_DATA
    && !canPartiallyEdit ? defaultValueStyle : styles.fieldValue;
    let emailError = null;
    if (this.state.email === '') {
      emailError = 'El email no puede ser vacío';
    } else if (!this.state.emailValid) {
      emailError = 'El email es inválido';
    }
    const namesError = this.state.names === '' ? 'Este campo no puede ser vacío' : null;
    const surnamesError = this.state.surnames === '' ? 'Este campo no puede ser vacío' : null;
    const tradeNameError = this.state.tradeName === '' ? 'Este campo no puede ser vacío' : null;
    const fictitiousNameError = this.state.fictitiousName === '' ? 'Este campo no puede ser vacío' : null;
    const namesValue = this.state.names !== null ? this.state.names : SessionStore.user.names;
    const surnamesValue = this.state.surnames !== null ? this.state.surnames
      : SessionStore.user.surnames;
    const emailValue = this.state.email !== null ? this.state.email : SessionStore.user.email;
    const tradeNameValue = this.state.tradeName !== null ? this.state.tradeName
      : SessionStore.user.tradeName;
    const fictitiousNameValue = this.state.fictitiousName !== null ? this.state.fictitiousName
      : SessionStore.user.fictitiousName;
    // eslint-disable-next-line prefer-destructuring
    const socialNetworksInstagramValue = this.state.socialNetworkInstagram !== null ?
      this.state.socialNetworkInstagram :
      SessionStore.socialNetworks.instagram;

    const isEditingData = this.state.actualScreen === SCREENS.EDIT_DATA;
    const isShowingData = this.state.actualScreen === SCREENS.SHOW_DATA;
    const isEditingPassword = this.state.actualScreen === SCREENS.EDIT_PASSWORD;
    const isEditingEmail = canPartiallyEdit && isEditingData;
    return (
      <div className={styles.container}>
        <SEO
          title="Mis datos"
          type="website"
        />
        <HomeNavbar />
        <div className={styles.mainContent}>
          {
            !mobileVersion &&
            <div className={styles.titleContainer}>
              <h1 className={styles.title}>Mi Cuenta</h1>
              <h2 className={styles.subtitle}>
                <span>Hola, </span>
                {SessionStore.user.type === USER_TYPES.COMPANY ?
                  SessionStore.user.fictitiousName :
                  SessionStore.user.names}
                !
              </h2>
            </div>
          }
          {
            mobileVersion &&
            <p className={styles.breadcrumb}>
              <AppLink
                className={styles.breadcrumb}
                routeName={routeNaming.MY_ACCOUNT_MENU_MOBILE}
              >
                Mi Cuenta
              </AppLink>
              <span> &gt; Mis Datos</span>
            </p>
          }
          <div className={styles.content}>
            {
              !mobileVersion && <MyAccountMenu indexSelectedItem={0} />
            }
            <div className={styles.myDataContainer}>
              <div className={styles.headerContainer}>
                <h2 className={styles.header}>
                  {isEditingData && 'Editar mis datos'}
                  {isShowingData && 'Mis datos'}
                  {isEditingPassword && 'Editar contraseña'}
                </h2>
                {
                  (SessionStore.user.isEditable || canPartiallyEdit) &&
                  <button className={styles.button} onClick={this._editOrCancel}>
                    {isShowingData ? 'EDITAR' : 'CANCELAR'}
                  </button>
                }
              </div>
              {
                !isEditingPassword && SessionStore.user.type !== USER_TYPES.COMPANY &&
                <div className={styles.myData}>
                  <p className={fieldNameStyle}>Nombre(s)</p>
                  <div className={styles.fieldValueContainer}>
                    <input
                      className={fieldValueStyle}
                      value={namesValue}
                      type="text"
                      name="names"
                      readOnly={!isEditingData || canPartiallyEdit}
                      onChange={this._changeInputValue}
                    />
                    {!!namesError &&
                      <p className={styles.error}>
                        {namesError}
                      </p>}
                  </div>
                  <p className={fieldNameStyle}>Apellido(s)</p>
                  <div className={styles.fieldValueContainer}>
                    <input
                      className={fieldValueStyle}
                      value={surnamesValue}
                      type="text"
                      name="surnames"
                      readOnly={!isEditingData || canPartiallyEdit}
                      onChange={this._changeInputValue}
                    />
                    {!!surnamesError &&
                      <p className={styles.error}>
                        {surnamesError}
                      </p>}
                  </div>
                  <p className={fieldNameStyle}>Email</p>
                  <div className={styles.fieldValueContainer}>
                    <input
                      className={isEditingEmail ? defaultValueStyle : fieldValueStyle}
                      value={emailValue}
                      type="text"
                      name="email"
                      readOnly={!isEditingData && !canPartiallyEdit}
                      onChange={this._updateEmail}
                    />
                    {!!emailError &&
                      <p className={styles.error}>
                        {emailError}
                      </p>}
                  </div>
                  <p className={fieldNameStyle}>Documento</p>
                  <div className={styles.fieldValueContainer}>
                    <input
                      className={fieldValueStyle}
                      value={SessionStore.user.document}
                      readOnly
                      disabled={isEditingData}
                    />
                  </div>
                  <p className={fieldNameStyle}>Contraseña</p>
                  <div className={styles.fieldValueContainer}>
                    {
                      isEditingData && !canPartiallyEdit ?
                        <button className={styles.button} onClick={this._editPassword}>
                          CAMBIAR CONTRASEÑA
                        </button> :
                        <input
                          className={styles.fieldValue}
                          value="********"
                          readOnly
                          disabled={isEditingData}
                        />
                    }
                  </div>
                  <p className={fieldNameStyle}>Usuario de instagram</p>
                  <div className={styles.fieldValueContainer}>
                    <input
                      className={isEditingData ? defaultValueStyle : styles.fieldValue}
                      value={socialNetworksInstagramValue}
                      type="text"
                      name="socialNetworkInstagram"
                      onChange={this._changeInputValue}
                      readOnly={!isEditingData}
                    />
                  </div>
                  {
                    SessionStore.user.rut && <p className={fieldNameStyle}>RUT</p>
                  }
                  {
                    SessionStore.user.rut &&
                    <div className={styles.fieldValueContainer}>
                      <input
                        className={fieldValueStyle}
                        value={SessionStore.user.rut}
                        readOnly
                        disabled={isEditingData}
                      />
                    </div>
                  }
                </div>
              }
              {
                !isEditingPassword && SessionStore.user.type === USER_TYPES.COMPANY &&
                <div className={styles.myData}>
                  <p className={fieldNameStyle}>Correo electrónico</p>
                  <div className={styles.fieldValueContainer}>
                    <input
                      className={isEditingEmail ? defaultValueStyle : fieldValueStyle}
                      value={emailValue}
                      type="text"
                      name="email"
                      readOnly={!isEditingData && !canPartiallyEdit}
                      onChange={this._updateEmail}
                    />
                    {!!emailError &&
                      <p className={styles.error}>
                        {emailError}
                      </p>}
                  </div>
                  <p className={fieldNameStyle}>Razón social</p>
                  <div className={styles.fieldValueContainer}>
                    <input
                      className={fieldValueStyle}
                      value={tradeNameValue}
                      type="text"
                      name="tradeName"
                      readOnly={!isEditingData || canPartiallyEdit}
                      onChange={this._changeInputValue}
                    />
                    {!!tradeNameError &&
                      <p className={styles.error}>
                        {tradeNameError}
                      </p>}
                  </div>
                  <p className={fieldNameStyle}>Nombre fantasía</p>
                  <div className={styles.fieldValueContainer}>
                    <input
                      className={fieldValueStyle}
                      value={fictitiousNameValue}
                      type="text"
                      name="fictitiousName"
                      readOnly={!isEditingData || canPartiallyEdit}
                      onChange={this._changeInputValue}
                    />
                    {!!fictitiousNameError &&
                      <p className={styles.error}>
                        {fictitiousNameError}
                      </p>}
                  </div>
                  <p className={fieldNameStyle}>RUT</p>
                  <div className={styles.fieldValueContainer}>
                    <input
                      className={fieldValueStyle}
                      value={SessionStore.user.rut}
                      readOnly
                      disabled={isEditingData}
                    />
                  </div>
                  <p className={fieldNameStyle}>Contraseña</p>
                  <div className={styles.fieldValueContainer}>
                    {
                      isEditingData && !canPartiallyEdit ?
                        <button className={styles.button} onClick={this._editPassword}>
                          CAMBIAR CONTRASEÑA
                        </button> :
                        <input className={styles.fieldValue} value="********" readOnly />
                    }
                  </div>
                  <p className={fieldNameStyle}>Usuario de instagram</p>
                  <div className={styles.fieldValueContainer}>
                    <input
                      className={isEditingData ? defaultValueStyle : styles.fieldValue}
                      value={socialNetworksInstagramValue}
                      type="text"
                      name="socialNetworkInstagram"
                      onChange={this._changeInputValue}
                      readOnly={!isEditingData}
                    />
                  </div>
                </div>
              }
              {
                isEditingPassword && <EditPassword callbackSuccess={this._editPasswordSuccess} />
              }
              {
                this.state.success &&
                <p className={styles.successMessage}>
                  Los datos se han guardado correctamente
                </p>
              }
              {
                SessionStore.updateError &&
                <p className={styles.errorMessage}>
                  Ha ocurrido un error al editar sus datos.
                  Por favor, inténtelo de nuevo.
                </p>
              }
              {
                SessionStore.cancelError &&
                <p className={styles.errorMessage}>
                  Para editar sus datos debe confirmar la contraseña.
                  Por favor, inténtelo de nuevo.
                </p>
              }
              {
                isEditingData &&
                <SecondaryButton
                  className={styles.saveButton}
                  onClick={this.saveChanges}
                >
                  GUARDAR CAMBIOS
                </SecondaryButton>
              }
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }
});

export { MyData };



// WEBPACK FOOTER //
// ./src/pages/my-account/my-data/MyData.jsx